import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { toast } from "sonner";
import {
  Mail,
  Eye,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  User,
  Bell,
  DollarSign,
  Clock,
  Key,
  AlertTriangle,
  FileText
} from "lucide-react";

const templateIcons = {
  EMAIL_VERIFICATION: Key,
  ACCOUNT_APPROVED: CheckCircle,
  ACCOUNT_NOT_APPROVED: XCircle,
  ADDITIONAL_EMAIL_VERIFICATION: Mail,
  NEW_TASKS_AVAILABLE: Bell,
  PASSWORD_RECOVERY: Key,
  PAYMENT_PROCESSING: DollarSign,
  PAYMENT_COMPLETED: CheckCircle,
  TASK_SUBMISSION_RECEIVED: FileText,
  TASK_SUBMISSION_APPROVED: CheckCircle,
  TASK_SUBMISSION_REJECTED: XCircle,
  WELCOME_EMAIL: User,
  ACCOUNT_SUSPENDED: AlertTriangle,
  PASSWORD_CHANGED: Key,
  INACTIVITY_REMINDER: Clock,
  WITHDRAWAL_REQUEST_RECEIVED: DollarSign,
  TASK_DEADLINE_REMINDER: Clock
};

const templateCategories = {
  "Account & Auth": [
    "EMAIL_VERIFICATION",
    "ACCOUNT_APPROVED",
    "ACCOUNT_NOT_APPROVED",
    "WELCOME_EMAIL",
    "ACCOUNT_SUSPENDED",
    "PASSWORD_RECOVERY",
    "PASSWORD_CHANGED",
    "ADDITIONAL_EMAIL_VERIFICATION"
  ],
  "Tasks": [
    "NEW_TASKS_AVAILABLE",
    "TASK_SUBMISSION_RECEIVED",
    "TASK_SUBMISSION_APPROVED",
    "TASK_SUBMISSION_REJECTED",
    "TASK_DEADLINE_REMINDER"
  ],
  "Payments": [
    "WITHDRAWAL_REQUEST_RECEIVED",
    "PAYMENT_PROCESSING",
    "PAYMENT_COMPLETED"
  ],
  "Engagement": [
    "INACTIVITY_REMINDER"
  ]
};

export default function AdminEmailTemplates({ token }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTestDialog, setShowTestDialog] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/v2/email-templates`, { headers });
      setTemplates(response.data.templates);
    } catch (error) {
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  };

  const previewTemplateContent = async (templateName) => {
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const response = await axios.get(
        `${API}/v2/email-templates/${templateName}/preview`,
        { headers }
      );
      setPreviewTemplate(response.data);
    } catch (error) {
      toast.error("Failed to load template preview");
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail || !selectedTemplate) return;
    
    setSendingTest(true);
    try {
      const response = await axios.post(
        `${API}/v2/email-templates/${selectedTemplate}/send-test?test_email=${encodeURIComponent(testEmail)}`,
        {},
        { headers }
      );
      if (response.data.mocked) {
        toast.warning("Email service not configured - test email simulated");
      } else {
        toast.success(`Test email sent to ${testEmail}`);
      }
      setShowTestDialog(false);
      setTestEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const getTemplatesByCategory = (categoryTemplates) => {
    return templates.filter(t => categoryTemplates.includes(t.name));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Email Templates</h2>
          <p className="text-sm text-slate-500">
            Preview and test all transactional email templates used by the platform
          </p>
        </div>
        <Badge variant="outline" className="text-lg px-3 py-1">
          {templates.length} Templates
        </Badge>
      </div>

      {/* Categories */}
      {Object.entries(templateCategories).map(([category, categoryTemplates]) => {
        const categoryItems = getTemplatesByCategory(categoryTemplates);
        if (categoryItems.length === 0) return null;

        return (
          <Card key={category}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                {category}
                <Badge variant="secondary" className="ml-2">
                  {categoryItems.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryItems.map((template) => {
                    const Icon = templateIcons[template.name] || Mail;
                    return (
                      <TableRow key={template.name}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                              <Icon className="w-4 h-4 text-primary" />
                            </div>
                            <span className="font-mono text-xs text-slate-600">
                              {template.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          {template.subject}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm max-w-xs truncate">
                          {template.description}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => previewTemplateContent(template.name)}
                              data-testid={`preview-${template.name}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedTemplate(template.name);
                                setShowTestDialog(true);
                              }}
                              data-testid={`test-${template.name}`}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Test
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Template Preview
              {previewTemplate && (
                <Badge variant="outline" className="ml-2">
                  {previewTemplate.template_name}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {previewTemplate?.subject}
            </DialogDescription>
          </DialogHeader>
          
          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : previewTemplate && (
            <div className="flex-1 overflow-auto border rounded-lg">
              <iframe
                srcDoc={previewTemplate.html}
                className="w-full h-[500px] border-0"
                title="Email Preview"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Test Email
            </DialogTitle>
            <DialogDescription>
              Send a test email using the {selectedTemplate} template
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="test-email">Recipient Email</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="your@email.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                data-testid="test-email-input"
              />
            </div>
            
            <p className="text-sm text-slate-500">
              The email will be sent with sample data to demonstrate the template.
            </p>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={sendTestEmail}
              disabled={!testEmail || sendingTest}
              data-testid="send-test-btn"
            >
              {sendingTest ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
